import React, { useState, useEffect } from 'react';
import { X, Coins, ShoppingCart, AlertCircle, CheckCircle } from 'lucide-react';
import { useTranslation } from "react-i18next";
import NotEnoughCoinsDialog from './NotEnoughCoinsDialog';
import {
  sendMessage,
  addListener,
  removeListener,
} from "../services/websocketManager";
import { generalMessageTemplate } from "../entities/generalMessageTemplate";

const PaymentConfirmationDialog = ({
  isOpen,
  onClose,
  onConfirm,
  onPurchaseCoins,
  title = "Confirm Payment",
  description = "Do you want to proceed with this purchase?",
  coinsRequired = 100,
  currentCoins = 50,
  feature = "Premium Feature",
  loading = false,
  userData = null,
  onWatchAd = null
}) => {
  const { t } = useTranslation();
  const [isProcessing, setIsProcessing] = useState(false);
  const [showNotEnoughCoins, setShowNotEnoughCoins] = useState(false);
  const hasEnoughCoins = currentCoins >= coinsRequired;

   useEffect(() => {
     
      // Define a function to handle WebSocket events
      const handleWebSocketEvent = (event) => {
        if (
          event.type === "message" &&
          (   
            event.message.messageType === "buy_via_coins_ok" ||
            event.message.messageType === "buy_via_coins_err" ||
            event.message.messageType === "not_enough_coins"
          )
        ) {
          if(event.message.messageType === "buy_via_coins_ok")
          {
              setIsProcessing(false);
              onConfirm();
          }
          else if(event.message.messageType === "buy_via_coins_err")
          {
            setIsProcessing(false);
          }
          else if(event.message.messageType === "not_enough_coins")
          {
            setIsProcessing(false);
            setShowNotEnoughCoins(true);
          }
        }
      };
      // Subscribe to WebSocket events
      addListener(handleWebSocketEvent);
       
  
      // Cleanup on component unmount
      return () => {
        removeListener(handleWebSocketEvent);
      };
    }, [onConfirm]);

  const handleConfirm = async () => {
    if (!hasEnoughCoins) return;
    
    setIsProcessing(true);

    const generalMessage = { ...generalMessageTemplate };
    generalMessage.messageType = "buy_via_coins";
    generalMessage.payment.price = coinsRequired;
    sendMessage(generalMessage);
 
    
  };

  const handlePurchaseCoins = () => {
    setShowNotEnoughCoins(true);
  };

  if (!isOpen) return null;

  return (
    <>
      {!showNotEnoughCoins && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-2 sm:p-4">
        {/* Backdrop */}
        <div 
          className="absolute inset-0 bg-black/50 backdrop-blur-sm"
          onClick={onClose}
        />
        
        {/* Dialog - Optimized for iPhone SE (320px) */}
        <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-sm mx-auto overflow-hidden max-h-[90vh] flex flex-col">
          {/* Header - Compact for mobile */}
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 px-4 py-3">
            <div className="flex items-center justify-between">
              <div className="w-6"></div> {/* Spacer for balance */}
              <h2 className="text-lg font-bold text-white text-center flex-1">{title}</h2>
              <button
                onClick={onClose}
                className="text-white/80 hover:text-white transition-colors flex-shrink-0"
                disabled={isProcessing || loading}
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Content - Scrollable on small screens */}
          <div className="p-4 flex-1 overflow-y-auto">
            {/* Feature Info - Compact */}
            <div className="text-center mb-4">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-100 to-purple-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <Coins className="w-6 h-6 text-blue-600" />
              </div>
              <h3 className="text-base font-semibold text-gray-900 mb-2 leading-tight">{feature}</h3>
              <p className="text-gray-600 text-xs leading-relaxed">{description}</p>
            </div>

            {/* Coins Section - Compact */}
            <div className="bg-gray-50 rounded-lg p-3 mb-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium text-gray-700">{t("Current Coins")}</span>
                <div className="flex items-center gap-1">
                  <Coins className="w-3 h-3 text-yellow-500" />
                  <span className="font-bold text-sm text-gray-900">{currentCoins.toLocaleString()}</span>
                </div>
              </div>
              
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium text-gray-700">{t("Required Coins")}</span>
                <div className="flex items-center gap-1">
                  <Coins className="w-3 h-3 text-blue-500" />
                  <span className="font-bold text-sm text-gray-900">{coinsRequired.toLocaleString()}</span>
                </div>
              </div>

              <hr className="my-2 border-gray-200" />

              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-gray-700">{t("After Purchase")}</span>
                <div className="flex items-center gap-1">
                  <Coins className="w-3 h-3 text-green-500" />
                  <span className={`font-bold text-sm ${hasEnoughCoins ? 'text-green-600' : 'text-red-500'}`}>
                    {hasEnoughCoins ? (currentCoins - coinsRequired).toLocaleString() : '0'}
                  </span>
                </div>
              </div>
            </div>

            {/* Status Message - Compact */}
            {!hasEnoughCoins && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-red-800 font-medium text-xs">{t("Insufficient Coins")}</p>
                    <p className="text-red-600 text-xs mt-1 leading-tight">
                      {t("You need")} {(coinsRequired - currentCoins).toLocaleString()} {t("more coins to complete this purchase")}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {hasEnoughCoins && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4">
                <div className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-green-800 font-medium text-xs">{t("Ready to Purchase")}</p>
                    <p className="text-green-600 text-xs mt-1 leading-tight">
                      {t("You have enough coins to complete this transaction")}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Action Buttons - Fixed at bottom, mobile optimized */}
          <div className="p-4 pt-2 border-t border-gray-100">
            {hasEnoughCoins ? (
              <div className="flex flex-col gap-2">
                <button
                  onClick={onClose}
                  className="w-full px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors text-sm"
                  disabled={isProcessing || loading}
                >
                  {t("Cancel")}
                </button>
                <button
                  onClick={handleConfirm}
                  disabled={isProcessing || loading}
                  className="w-full px-4 py-3 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white rounded-lg font-medium transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm"
                >
                  {isProcessing || loading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      {t("Processing...")}
                    </>
                  ) : (
                    <>
                      <Coins className="w-4 h-4" />
                      {t("Confirm Purchase")}
                    </>
                  )}
                </button>
              </div>
            ) : (
              <div className="flex flex-col gap-2">
                <button
                  onClick={onClose}
                  className="w-full px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors text-sm"
                  disabled={isProcessing || loading}
                >
                  {t("Cancel")}
                </button>
                <button
                  onClick={handlePurchaseCoins}
                  className="w-full px-4 py-3 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white rounded-lg font-medium transition-all duration-200 flex items-center justify-center gap-2 text-sm"
                  disabled={isProcessing || loading}
                >
                  <ShoppingCart className="w-4 h-4" />
                  {t("Buy Coins")}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
      )}

      <NotEnoughCoinsDialog
        open={showNotEnoughCoins}
        onClose={() => setShowNotEnoughCoins(false)}
        onWatchAd={onWatchAd}
        neededCoins={coinsRequired}
        actualCoins={currentCoins}
        buy={true}
        userData={userData}
        scrollToCoins={false}
      />
    </>
  );
};

export default PaymentConfirmationDialog;